import { call } from "redux-saga/effects";

import { backOfficeApiService } from "../../../../actionService/apiService";

import endPoints from "./applications.endpoints";

import {
  AiAutofillRequestDto,
  AiAutofillResponseDto,
  AiAutofillUploadRequestDto,
  AiAutofillInputRequestDto,
  CreateApplicationRequestDto,
  CopyCalculationFromRequestDto,
  GetFilterLiteRequestDto,
  GetSalesIndicativePreviewRequestDto,
  UpdateLiteRequestDto,
  GetApplicationByIdResponseDto,
  GetApplicationRecommendationsRequestDto,
  UpdateApplicationCalculationsRequestDto,
  UpdateApplicationRequestDto,
  WaitForApplicationUpdateRequestDto,
  GetCargoPlanningStatusRequestDto,
  CreateCargoPlanningEmbedLinkRequestDto,
} from "./applications.requestsDto";

import { ApiCallResult } from "../../../../types";

import { ApplicationDto, ApplicationLiteDto, ApplicationMatchDto } from "../../../../../dto/Applications/ApplicationDto";
import { SalesIndicativeDto } from "../../../../../dto/Applications/SalesIndicativeDto";
import { ApplicationRecommendationsDto } from "../../../../../dto/Applications/ApplicationRecommendationsDto";
import { ApplicationDataConverter } from "../../../../../viewModels/Applications/ApplicationModel";
import type { CargoPlanningEmbedLinkDto, CargoPlanningStatusDto } from "../../../../../dto/Applications/CargoPlanningDto";

const backOfficeApplicationsApi = {
  getCargoPlanningStatus: function* ({ applicationId, abortController }: GetCargoPlanningStatusRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.cargoPlanningStatus(applicationId),
      undefined,
      undefined,
      abortController,
    )) as ApiCallResult<CargoPlanningStatusDto>;
  },
  createCargoPlanningEmbedLink: function* ({ applicationId, abortController }: CreateCargoPlanningEmbedLinkRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Post],
      endPoints.cargoPlanningEmbedLink(applicationId),
      undefined,
      undefined,
      abortController,
    )) as ApiCallResult<CargoPlanningEmbedLinkDto>;
  },
  copyCalculationFrom: function* (dto: CopyCalculationFromRequestDto) {
    const { sourceApplicationId, sourceCalculationId, requestId } = dto;
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.copyCalculationFrom(dto), {
      sourceApplicationId,
      sourceCalculationId,
      requestId,
    })) as ApiCallResult<ApplicationDto>;
  },
  getById: function* (id: number) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.byId(id),
    )) as ApiCallResult<GetApplicationByIdResponseDto>;
  },
  update: function* (dto: UpdateApplicationRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Put], endPoints.byId(dto.id), undefined, {
      ...dto,
      filter: ApplicationDataConverter.converFilterForUpdate(dto.filter),
    })) as ApiCallResult<ApplicationDto>;
  },
  updateCalculations: function* (dto: UpdateApplicationCalculationsRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Put], endPoints.calculations(dto.id), undefined, {
      calculations: dto.calculations,
    })) as ApiCallResult<ApplicationDto>;
  },
  getMatching: function* (id: number) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.matching(id))) as ApiCallResult<
      Array<ApplicationMatchDto>
    >;
  },
  getRecommendations: function* (dto: GetApplicationRecommendationsRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.recommendations(dto.applicationId),
      undefined,
      undefined,
      dto.abortController,
    )) as ApiCallResult<ApplicationRecommendationsDto>;
  },
  waitForUpdate: function* (dto: WaitForApplicationUpdateRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.waitForUpdate(dto.id),
      undefined,
      { tabId: dto.tabId },
      dto.abortController,
    )) as ApiCallResult<ApplicationDto | { status: 204 }>;
  },
  create: function* (dto: CreateApplicationRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.controller, dto)) as ApiCallResult<ApplicationDto>;
  },
  getLite: function* (dto: GetFilterLiteRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.filterLite, undefined, dto)) as ApiCallResult<{
      data: Array<ApplicationLiteDto>;
      total: number;
    }>;
  },
  export: function* (dto: GetFilterLiteRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.export, undefined, dto)) as ApiCallResult;
  },
  updateLite: function* (dto: UpdateLiteRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Put],
      endPoints.lite(dto.id),
      undefined,
      dto,
    )) as ApiCallResult<ApplicationLiteDto>;
  },
  syncClientInfo: function* (id: number) {
    return (yield call([backOfficeApiService, backOfficeApiService.Put], endPoints.clientInfo(id))) as ApiCallResult<ApplicationDto>;
  },
  delete: function* (ids: Array<number>) {
    return (yield call([backOfficeApiService, backOfficeApiService.Delete], endPoints.controller, ids.join(","))) as ApiCallResult;
  },
  copy: function* (ids: Array<number>, transportCharacteristicId: number) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.controller, undefined, ids.join(","), {
      transportCharacteristicId,
    })) as ApiCallResult;
  },
  aiAutofill: function* (dto: AiAutofillRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Post],
      endPoints.aiAutofill(dto.id),
      dto,
    )) as ApiCallResult<AiAutofillResponseDto>;
  },
  aiAutofillInput: function* (dto: AiAutofillInputRequestDto) {
    const body = new FormData();
    body.append("message", dto.message);
    dto.files.forEach((file) => body.append("files", file, file.name));
    return (yield call([backOfficeApiService, backOfficeApiService.PostMultipartForm], endPoints.aiAutofillInput(dto.id), body, undefined, {
      logId: dto.logId,
    })) as ApiCallResult<AiAutofillResponseDto>;
  },
  aiAutofillDocument: function* (dto: AiAutofillUploadRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.PostMultipartForm],
      endPoints.aiAutofillDocument(dto.id),
      { file: dto.file },
      undefined,
      { logId: dto.logId },
    )) as ApiCallResult<AiAutofillResponseDto>;
  },
  getSalesIndicativePreview: function* (dto: GetSalesIndicativePreviewRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.salesIndicatives,
      undefined,
      dto,
    )) as ApiCallResult<SalesIndicativeDto>;
  },
};

export default backOfficeApplicationsApi;
